import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Mail, Phone, MapPin } from "lucide-react";
import { FORMATIONS, NDA_LABEL, QUALITE_LABEL } from "./Shared";

const NAV = [
  { to: "/", label: "Accueil", testid: "nav-accueil" },
  { to: "/livres", label: "Livres", testid: "nav-livres" },
  { to: "/dico-ia", label: "Dico IA", testid: "nav-dico-ia" },
  { to: "/tablecho", label: "Tablecho", testid: "nav-tablecho" },
  { to: "/qui-suis-je", label: "Qui suis-je", testid: "nav-qui-suis-je" },
  { to: "/references", label: "Références", testid: "nav-references" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
];

const linkCls = ({ isActive }) =>
  `font-sans text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-vert" : "text-white/75 hover:text-white"
  }`;

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-marine/95 backdrop-blur border-b border-white/10">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo" className="font-serif font-semibold text-xl text-white shrink-0">
          Form&nbsp;Action&nbsp;<span className="text-vert">Pro</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6" aria-label="Navigation principale">
          <NavLink to="/" data-testid="nav-accueil" className={linkCls} end>
            Accueil
          </NavLink>
          <div className="relative group">
            <button
              data-testid="nav-formations"
              className="flex items-center gap-1 font-sans text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
            >
              Formations <ChevronDown size={14} />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="bg-white rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.15)] border border-bordure py-2 w-80">
                {FORMATIONS.map((f) => (
                  <Link
                    key={f.to}
                    to={f.to}
                    data-testid={`nav-${f.to.split("/").pop()}`}
                    className="block px-5 py-2.5 font-sans text-sm text-marine hover:bg-sable transition-colors duration-150"
                  >
                    {f.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} className={linkCls}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-devis-btn"
            className="hidden sm:inline-block bg-vert hover:bg-vert-hover text-marine font-sans font-semibold text-sm px-5 py-2.5 rounded transition-colors duration-200"
          >
            Demander un devis
          </Link>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          data-testid="mobile-menu"
          className="lg:hidden bg-marine border-t border-white/10 px-6 py-6 space-y-1"
          aria-label="Navigation mobile"
        >
          <NavLink to="/" data-testid="mnav-accueil" className="block py-2.5 font-sans text-base text-white/85 hover:text-vert" end>
            Accueil
          </NavLink>
          <p className="pt-3 pb-1 font-sans text-xs font-semibold uppercase tracking-[2px] text-vert">Formations</p>
          {FORMATIONS.map((f) => (
            <Link
              key={f.to}
              to={f.to}
              data-testid={`mnav-${f.to.split("/").pop()}`}
              className="block py-2 pl-4 font-sans text-base text-white/85 hover:text-vert"
            >
              {f.label}
            </Link>
          ))}
          <div className="pt-3 space-y-1">
            {NAV.slice(1).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`m${n.testid}`}
                className="block py-2.5 font-sans text-base text-white/85 hover:text-vert"
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="mnav-devis-btn"
            className="mt-4 block text-center bg-vert hover:bg-vert-hover text-marine font-sans font-semibold px-5 py-3 rounded transition-colors duration-200"
          >
            Demander un devis
          </Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-marine-deep text-white">
      <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <p className="font-serif font-semibold text-xl mb-4">
            Form Action <span className="text-vert">Pro</span>
          </p>
          <p className="font-sans text-sm text-white/60 leading-relaxed mb-5">
            Organisme de formation professionnelle. IA &amp; no-code, hygiène alimentaire &amp;
            Plan de Maîtrise Sanitaire, audit d’établissement.
          </p>
          <div className="space-y-2 font-sans text-sm text-white/75">
            <p className="flex items-center gap-2">
              <Mail size={15} className="text-vert" />
              <a href="mailto:form.action1pro@gmail.com" data-testid="footer-email" className="hover:text-vert transition-colors">
                form.action1pro@gmail.com
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Phone size={15} className="text-vert" />
              <a href="tel:+33769057673" data-testid="footer-phone" className="hover:text-vert transition-colors">
                07 69 05 76 73
              </a>
            </p>
            <p className="flex items-center gap-2">
              <MapPin size={15} className="text-vert" /> La Croix Valmer (Var, 83)
            </p>
          </div>
        </div>
        <div>
          <p className="font-sans text-xs font-semibold uppercase tracking-[2px] text-vert mb-4">Formations</p>
          <ul className="space-y-2.5 font-sans text-sm text-white/70">
            {FORMATIONS.map((f) => (
              <li key={f.to}>
                <Link to={f.to} data-testid={`footer-${f.to.split("/").pop()}`} className="hover:text-vert transition-colors">
                  {f.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/contact" data-testid="footer-audit" className="hover:text-vert transition-colors">
                Audit d’établissement
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-sans text-xs font-semibold uppercase tracking-[2px] text-vert mb-4">Ressources</p>
          <ul className="space-y-2.5 font-sans text-sm text-white/70">
            <li><Link to="/livres" data-testid="footer-livres" className="hover:text-vert transition-colors">Livres &amp; ressources</Link></li>
            <li>
              <a
                href="https://francois-schoonheere.tinypages.co/livres"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-mes-livres"
                className="hover:text-vert transition-colors"
              >
                Mes livres (boutique)
              </a>
            </li>
            <li><Link to="/dico-ia" data-testid="footer-dico" className="hover:text-vert transition-colors">Le petit dico de l’IA</Link></li>
            <li><Link to="/tablecho" data-testid="footer-tablecho" className="hover:text-vert transition-colors">Tablecho</Link></li>
            <li><Link to="/references" data-testid="footer-references" className="hover:text-vert transition-colors">Références</Link></li>
            <li><Link to="/faq" data-testid="footer-faq" className="hover:text-vert transition-colors">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-sans text-xs font-semibold uppercase tracking-[2px] text-vert mb-4">Informations</p>
          <ul className="space-y-2.5 font-sans text-sm text-white/70">
            <li><Link to="/qui-suis-je" data-testid="footer-qui-suis-je" className="hover:text-vert transition-colors">Qui suis-je</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-vert transition-colors">Contact &amp; devis</Link></li>
            <li><Link to="/mentions-legales" data-testid="footer-mentions" className="hover:text-vert transition-colors">Mentions légales</Link></li>
            <li><Link to="/confidentialite" data-testid="footer-confidentialite" className="hover:text-vert transition-colors">Politique de confidentialité</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-3">
          <p data-testid="footer-nda" className="font-sans text-xs text-white/50 leading-relaxed">
            {NDA_LABEL}
          </p>
          <p data-testid="footer-qualite" className="font-sans text-xs text-white/50 leading-relaxed">
            {QUALITE_LABEL}
          </p>
          <p className="font-sans text-xs text-white/40">© 2026 Form Action Pro — Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
